import React, { useState, useEffect } from 'react';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';

export const inputClass =
  'w-full px-3 py-2 bg-cream border border-ink/10 rounded-lg font-medium text-sm focus:outline-none focus:bg-lime/20 placeholder:text-ink/40';

export const SectionCard = ({ title, icon: Icon, children, action, subtitle }) => (
  <div className="bg-white border border-ink/10 rounded-lg shadow-saas mb-6">
    <div className="p-5 border-b border-ink/10 flex items-center justify-between gap-3">
      <div>
        <h2 className="font-bold tracking-tight text-xl flex items-center gap-2">
          {Icon && <Icon size={20} />}
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-1">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

export const SaveBar = ({ onSave, saving, dirty, label = 'Save Section' }) => (
  <div className="bg-white border border-ink/10 rounded-lg shadow-saas mb-6 p-4 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10">
    <div className="text-sm font-bold uppercase tracking-wide text-ink/60">
      {dirty ? 'Unsaved changes' : 'All changes saved'}
    </div>
    <button
      onClick={onSave}
      disabled={saving || !dirty}
      className="inline-flex items-center px-6 py-3 bg-ink text-white border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-50"
    >
      {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
      {saving ? 'Saving...' : label}
    </button>
  </div>
);

export const TextArea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea
    rows={rows}
    value={value || ''}
    onChange={onChange}
    placeholder={placeholder}
    className={inputClass}
  />
);

export const TextInput = ({ value, onChange, placeholder }) => (
  <input
    value={value || ''}
    onChange={onChange}
    placeholder={placeholder}
    className={inputClass}
  />
);

export const Field = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="block text-xs font-bold uppercase tracking-wide text-ink/60 mb-1">{label}</label>
    {hint && <p className="text-xs font-medium text-ink/50 mb-2">{hint}</p>}
    {children}
  </div>
);

export const StringListEditor = ({ items, onChange, placeholder, addLabel = 'Add Item' }) => {
  const list = items || [];
  const update = (i, val) => onChange(list.map((it, idx) => (idx === i ? val : it)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, '']);
  return (
    <div className="space-y-2">
      {list.map((it, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="bg-lime border border-ink/10 rounded-lg px-2 py-1.5 font-bold text-xs flex-shrink-0">
            {i + 1}
          </span>
          <input
            value={it}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder}
            className={inputClass}
          />
          <button
            onClick={() => remove(i)}
            className="p-2 bg-red-500 text-white border border-ink/10 rounded-lg flex-shrink-0"
            aria-label="Remove item"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {list.length === 0 && (
        <p className="text-sm font-bold uppercase tracking-wide text-ink/40">Nothing added yet</p>
      )}
      <button
        onClick={add}
        className="inline-flex items-center px-3 py-2 bg-white border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-xs tracking-wide transition-transform"
      >
        <Plus size={14} className="mr-1" /> {addLabel}
      </button>
    </div>
  );
};

export const ObjectListEditor = ({ items, onChange, fields, addLabel = 'Add Row' }) => {
  const list = items || [];
  const blank = () => Object.fromEntries(fields.map((f) => [f.key, '']));
  const update = (i, key, val) => onChange(list.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, blank()]);
  return (
    <div className="space-y-3">
      {list.map((it, i) => (
        <div key={i} className="bg-cream border border-ink/10 rounded-lg p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="bg-lime border border-ink/10 rounded-lg px-2 py-0.5 font-bold text-xs">{i + 1}</span>
            <button
              onClick={() => remove(i)}
              className="p-1.5 bg-red-500 text-white border border-ink/10 rounded-lg"
              aria-label="Remove row"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-ink/50 mb-1">
                  {f.label}
                </label>
                {f.textarea ? (
                  <textarea
                    rows={2}
                    value={it[f.key] || ''}
                    onChange={(e) => update(i, f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className={inputClass}
                  />
                ) : (
                  <input
                    value={it[f.key] || ''}
                    onChange={(e) => update(i, f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className={inputClass}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {list.length === 0 && (
        <p className="text-sm font-bold uppercase tracking-wide text-ink/40">Nothing added yet</p>
      )}
      <button
        onClick={add}
        className="inline-flex items-center px-3 py-2 bg-white border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-xs tracking-wide transition-transform"
      >
        <Plus size={14} className="mr-1" /> {addLabel}
      </button>
    </div>
  );
};

// Keeps a local editable copy of a derived section object. Re-syncs whenever the
// saved value (from the server) changes. `dirty` flags pending edits.
export const useDraft = (initial) => {
  const key = JSON.stringify(initial);
  const [draft, setDraft] = useState(initial);
  useEffect(() => {
    setDraft(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const dirty = JSON.stringify(draft) !== key;
  return [draft, setDraft, dirty];
};
